/**
 * `/newsletter`'s offer picker (whatsapp-newsletter-audiences, 2026-10-29):
 * which of the studio's discount codes an "Article + offer" message may
 * offer, how each reads in the dropdown, and the offer line a picked code
 * starts the message with. Pure: the page renders what these return, and
 * `whatsapp-newsletter-offer.test.ts` pins them.
 *
 * The line is a starting point staff edit, never the source of truth — what
 * is sent is what the box holds when Send is pressed. It finishes the
 * template's "This week only: {{3}}." sentence, so it has no full stop of
 * its own.
 *
 * The wire shape mirrors one coupon of `list_stripe_coupons`
 * (`packages/canonical-actions/src/actions/list-stripe-coupons.ts`), only the
 * fields read here.
 */

import { fmtMoney } from "./format-money";

export interface OfferCoupon {
  readonly coupon_id: string;
  readonly name: string | null;
  readonly percent_off: number | null;
  readonly amount_off: number | null;
  readonly currency: string | null;
  readonly valid: boolean;
  /** `packages` | `memberships` | `both`; null on codes minted before the model. */
  readonly applies_to: string | null;
  /** Specific catalog items the code covers; null = its whole class(es). */
  readonly applies_to_slugs: ReadonlyArray<string> | null;
  /** The last moment it can be redeemed (ISO-8601); null = no end date. */
  readonly redeem_by: string | null;
  /** The member it belongs to; null = anyone may spend it. */
  readonly member_id: string | null;
}

/**
 * The codes a newsletter may offer: valid now, for anyone — never one
 * member's own code, which nobody else could spend — and not past its end.
 * Stripe's order (newest first) is kept.
 */
export function offerableCoupons(
  coupons: ReadonlyArray<OfferCoupon>,
  now: Date,
): ReadonlyArray<OfferCoupon> {
  return coupons.filter(
    (c) =>
      c.valid &&
      c.member_id === null &&
      (c.redeem_by === null || Date.parse(c.redeem_by) > now.getTime()),
  );
}

/** "15% off" / "£20 off"; null for a code with neither (none is minted so). */
export function offerDiscountText(c: OfferCoupon): string | null {
  if (c.percent_off !== null) return `${String(c.percent_off)}% off`;
  // Whole pounds read as "£20", not "£20.00", in a marketing line.
  if (c.amount_off !== null) return `${fmtMoney(c.amount_off, c.currency).replace(/\.00$/, "")} off`;
  return null;
}

/** What the code may be spent on, as it finishes "15% off …". */
function offerScopeText(c: OfferCoupon): string {
  const selected = c.applies_to_slugs !== null && c.applies_to_slugs.length > 0 ? "selected " : "";
  if (c.applies_to === "packages") return `${selected}packages`;
  if (c.applies_to === "memberships") return `${selected}memberships`;
  return `${selected}packages and memberships`;
}

function offerEndDate(iso: string, timeZone: string, style: "long" | "short"): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone,
    weekday: style,
    day: "numeric",
    month: style,
  }).format(new Date(iso));
}

/**
 * The offer line a picked code starts with — "15% off packages and
 * memberships with code OCTOBER15, until Saturday 31 October". The end date
 * is the studio's calendar day of `redeem_by`.
 */
export function offerLineFromCoupon(c: OfferCoupon, timeZone: string): string {
  const discount = offerDiscountText(c);
  const what = discount === null ? offerScopeText(c) : `${discount} ${offerScopeText(c)}`;
  const until = c.redeem_by === null ? "" : `, until ${offerEndDate(c.redeem_by, timeZone, "long")}`;
  return `${what} with code ${c.coupon_id}${until}`;
}

/** The dropdown's right-hand note — "15% off · until Sat 31 Oct". */
export function offerOptionMeta(c: OfferCoupon, timeZone: string): string {
  const until =
    c.redeem_by === null ? "no end date" : `until ${offerEndDate(c.redeem_by, timeZone, "short")}`;
  const discount = offerDiscountText(c);
  return discount === null ? until : `${discount} · ${until}`;
}
